function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function text(value) {
  return String(value ?? '').trim();
}

function memberKey(record = {}) {
  return text(record.member_openid ?? record.memberOpenid ?? record.openid ?? record.member_nickname ?? record.nickname);
}

function memberNickname(record = {}) {
  return text(record.member_nickname ?? record.memberNickname ?? record.nickname) || '닉네임 확인 불가';
}

function recordTime(record = {}) {
  return Date.parse(record.attacked_at ?? record.attackedAt ?? record.updated_at ?? record.updatedAt ?? '') || 0;
}

function deckLevel(record = {}) {
  const levels = (Array.isArray(record.deck) ? record.deck : [])
    .map((item) => numberValue(item?.level ?? item?.lv ?? item?.char_level ?? item?.charLevel, 0))
    .filter((level) => level > 0);
  if (!levels.length) return 0;
  const counts = new Map();
  for (const level of levels) counts.set(level, (counts.get(level) || 0) + 1);
  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1] || right[0] - left[0])[0]?.[0] || 0;
}

function settlementLevel(record = {}) {
  return deckLevel(record) || numberValue(record.char_level ?? record.charLevel, 0);
}

export function buildSettlementLevelHistory({
  seasons = [],
  records = [],
  members = [],
  selectedSeasonNo = null,
  currentMembersOnly = false,
} = {}) {
  const seasonRows = (Array.isArray(seasons) ? seasons : [])
    .map((season) => ({
      id: text(season.id ?? season.season_id),
      seasonNo: numberValue(season.season_no ?? season.seasonNo),
    }))
    .filter((season) => season.id && season.seasonNo > 0)
    .sort((a, b) => a.seasonNo - b.seasonNo);
  const seasonById = new Map(seasonRows.map((season) => [season.id, season]));
  const levelsBySeason = new Map();
  const nicknameByMember = new Map();
  const currentMemberKeys = new Set();

  // 현재 명단은 표시 대상과 최신 닉네임의 기준이다.
  // 성장 수치는 실전 덱에 저장된 캐릭터 level을 우선 사용한다.
  for (const member of Array.isArray(members) ? members : []) {
    const key = memberKey(member);
    if (!key) continue;
    currentMemberKeys.add(key);
    nicknameByMember.set(key, memberNickname(member));
  }

  for (const record of Array.isArray(records) ? records : []) {
    const season = seasonById.get(text(record.season_id ?? record.seasonId));
    const level = settlementLevel(record);
    const key = memberKey(record);
    if (!season || !key || level <= 0) continue;
    if (currentMembersOnly && !currentMemberKeys.has(key)) continue;

    const nickname = memberNickname(record);
    if (!nicknameByMember.has(key)) nicknameByMember.set(key, nickname);
    const byMember = levelsBySeason.get(season.seasonNo) || new Map();
    const current = byMember.get(key);
    const time = recordTime(record);
    if (!current || time >= current.time) {
      byMember.set(key, {
        memberKey: key,
        nickname: nicknameByMember.get(key) || nickname,
        level,
        time,
      });
    }
    levelsBySeason.set(season.seasonNo, byMember);
  }

  const points = seasonRows.map((season) => {
    const seasonMembers = [...(levelsBySeason.get(season.seasonNo)?.values() || [])];
    const levelTotal = seasonMembers.reduce((sum, member) => sum + member.level, 0);
    return {
      seasonNo: season.seasonNo,
      memberCount: seasonMembers.length,
      averageLevel: seasonMembers.length ? levelTotal / seasonMembers.length : 0,
      maxLevel: seasonMembers.reduce((max, member) => Math.max(max, member.level), 0),
      members: seasonMembers,
    };
  }).filter((point) => point.memberCount > 0);

  const selectedNo = numberValue(selectedSeasonNo, points.at(-1)?.seasonNo || 0);
  const selectedIndex = points.findIndex((point) => point.seasonNo === selectedNo);
  const current = selectedIndex >= 0 ? points[selectedIndex] : points.at(-1) || null;
  const previous = current
    ? points.slice(0, points.findIndex((point) => point.seasonNo === current.seasonNo)).at(-1) || null
    : null;
  const previousByMember = new Map((previous?.members || []).map((member) => [member.memberKey, member]));
  const changes = (current?.members || [])
    .map((member) => {
      const prior = previousByMember.get(member.memberKey);
      return {
        ...member,
        previousLevel: prior?.level || null,
        delta: prior ? member.level - prior.level : null,
      };
    })
    .sort((a, b) => numberValue(b.delta, -99999) - numberValue(a.delta, -99999)
      || b.level - a.level
      || a.nickname.localeCompare(b.nickname, 'ko-KR'));

  const seasonNos = points.map((point) => point.seasonNo);
  const allMemberKeys = new Set();
  for (const point of points) {
    for (const member of point.members) allMemberKeys.add(member.memberKey);
  }
  const currentByMember = new Map((current?.members || []).map((member) => [member.memberKey, member]));
  const memberRows = [...allMemberKeys]
    .map((key) => {
      const values = Object.fromEntries(seasonNos.map((seasonNo) => [
        seasonNo,
        levelsBySeason.get(seasonNo)?.get(key)?.level || null,
      ]));
      const currentLevel = values[current?.seasonNo] || null;
      const previousLevel = values[previous?.seasonNo] || null;
      return {
        memberKey: key,
        nickname: nicknameByMember.get(key) || currentByMember.get(key)?.nickname || '닉네임 확인 불가',
        values,
        currentLevel,
        previousLevel,
        delta: currentLevel && previousLevel ? currentLevel - previousLevel : null,
      };
    })
    .sort((a, b) => numberValue(b.currentLevel) - numberValue(a.currentLevel)
      || numberValue(b.delta, -99999) - numberValue(a.delta, -99999)
      || a.nickname.localeCompare(b.nickname, 'ko-KR'));

  return {
    points,
    current,
    previous,
    changes,
    seasonNos,
    memberRows,
    averageDelta: current && previous ? current.averageLevel - previous.averageLevel : null,
  };
}
